import React, { useState, useEffect } from 'react';
import type { PaymentStatus, PaymentUIState } from '../../../types/payment';

const MOBILE_BREAKPOINT = 720;

const isWindowMobile = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth < MOBILE_BREAKPOINT;
};

export interface PaymentStatusDisplayProps {
  /** Current payment status from backend */
  status: PaymentStatus;
  /** Optional UI state for additional context */
  uiState?: PaymentUIState;
  /** Payment amount to display */
  amount?: string;
  /** Payment currency (POL, USDT) */
  currency?: string;
  /** Expiration timestamp in ISO format */
  expiresAt?: string;
  /** Callback for retry action (FAILED status) */
  onRetry?: () => void;
  /** Callback for create new payment (EXPIRED status) */
  onCreateNew?: () => void;
  /** Callback for back navigation */
  onBack?: () => void;
  /** Callback for contact support */
  onContact?: () => void;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Configuration for status display UI elements.
 * Task 8.2: Status mapping interface.
 */
export interface StatusConfig {
  icon: string;
  color: 'success' | 'error' | 'warning' | 'info';
  title: string;
  description: string;
  showRetry: boolean;
  showCreateNew: boolean;
  showBack: boolean;
  showContact: boolean;
}

/**
 * Maps PaymentStatus to UI configuration.
 * Task 8.2: Status mapping function.
 */
export const getStatusConfig = (status: PaymentStatus): StatusConfig => {
  const configs: Record<PaymentStatus, StatusConfig> = {
    PENDING: {
      icon: '⏳',
      color: 'info',
      title: 'Oczekiwanie na płatność...',
      description: 'Czekamy na potwierdzenie płatności.',
      showRetry: false,
      showCreateNew: false,
      showBack: false,
      showContact: false,
    },
    CONFIRMED: {
      icon: '✓',
      color: 'success',
      title: 'Płatność potwierdzona!',
      description: 'Twoja płatność została pomyślnie zrealizowana.',
      showRetry: false,
      showCreateNew: false,
      showBack: true,
      showContact: false,
    },
    FAILED: {
      icon: '✗',
      color: 'error',
      title: 'Płatność nie powiodła się',
      description: 'Wystąpił błąd podczas przetwarzania płatności.',
      showRetry: true,
      showCreateNew: false,
      showBack: false,
      showContact: true,
    },
    EXPIRED: {
      icon: '⚠',
      color: 'warning',
      title: 'Płatność wygasła',
      description: 'Czas na dokonanie płatności upłynął.',
      showRetry: false,
      showCreateNew: true,
      showBack: false,
      showContact: false,
    },
    REFUNDED: {
      icon: 'ℹ',
      color: 'info',
      title: 'Płatność zwrócona',
      description: 'Środki zostały zwrócone na Twoje konto.',
      showRetry: false,
      showCreateNew: false,
      showBack: true,
      showContact: false,
    },
    CHARGEBACKED: {
      icon: 'ℹ',
      color: 'info',
      title: 'Płatność obciążona zwrotnie',
      description: 'Transakcja została anulowana przez bank.',
      showRetry: false,
      showCreateNew: false,
      showBack: true,
      showContact: true,
    },
    USED: {
      icon: 'ℹ',
      color: 'info',
      title: 'Płatność już wykorzystana',
      description: 'Ta płatność została już wykorzystana.',
      showRetry: false,
      showCreateNew: false,
      showBack: true,
      showContact: false,
    },
  };

  return configs[status];
};

/**
 * Get icon CSS classes based on status.
 * Task 8.3: Animation classes.
 */
const getIconClasses = (status: PaymentStatus): string => {
  const classes = ['animated-icon'];
  if (status === 'PENDING') {
    classes.push('spinner');
  }
  return classes.join(' ');
};

/**
 * PaymentStatusDisplay component
 * Displays payment status with appropriate UI feedback.
 * Task 8.1: Basic structure with TypeScript interfaces.
 * Task 8.2: Status mapping and UI rendering.
 * Task 8.3: Animations for status transitions.
 */
const PaymentStatusDisplay: React.FC<PaymentStatusDisplayProps> = ({
  status,
  uiState,
  amount,
  currency,
  expiresAt,
  onRetry,
  onCreateNew,
  onBack,
  onContact,
  className,
}) => {
  const config = getStatusConfig(status);
  const [isMobile, setIsMobile] = useState(isWindowMobile);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const layoutType = isMobile ? 'mobile' : 'desktop';
  const actionsLayout = isMobile ? 'stack' : 'row';

  return (
    <div
      className={className}
      data-testid="payment-status-display"
      data-animated="true"
      data-status-key={status}
      data-layout={layoutType}
      role="status"
      aria-live="polite"
      aria-busy={status === 'PENDING' ? 'true' : 'false'}
    >
      <span
        data-testid="status-icon"
        data-status={status}
        data-color={config.color}
        className={getIconClasses(status)}
      >
        {config.icon}
      </span>
      <h3>{config.title}</h3>
      <p>{config.description}</p>
      
      {amount && currency && (
        <div data-testid="payment-amount">
          <span>{amount}</span> <span>{currency}</span>
        </div>
      )}

      {expiresAt && (
        <div data-testid="payment-expires">
          <span>Wygasa: {new Date(expiresAt).toLocaleString('pl-PL')}</span>
        </div>
      )}

      <div data-testid="payment-actions" className="animated-actions" data-layout={actionsLayout}>
        {config.showRetry && onRetry && (
          <button 
            type="button" 
            onClick={onRetry} 
            aria-label="Ponów płatność"
            data-fullwidth={isMobile ? 'true' : undefined}
          >
            Ponów płatność
          </button>
        )}
        {config.showCreateNew && onCreateNew && (
          <button 
            type="button" 
            onClick={onCreateNew} 
            aria-label="Utwórz nową płatność"
            data-fullwidth={isMobile ? 'true' : undefined}
          >
            Nowa płatność
          </button>
        )}
        {config.showBack && onBack && (
          <button 
            type="button" 
            onClick={onBack} 
            aria-label="Wróć do poprzedniej strony"
            data-fullwidth={isMobile ? 'true' : undefined}
          >
            Wróć
          </button>
        )}
        {config.showContact && onContact && (
          <button 
            type="button" 
            onClick={onContact} 
            aria-label="Skontaktuj się z pomocą"
            data-fullwidth={isMobile ? 'true' : undefined}
          >
            Kontakt
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusDisplay;
